import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import swal from "sweetalert";
import { saveAs } from "file-saver";
import axios from "axios";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeading from "../../Components/PageHeading/PageHeading";
import SampleImageModal from "../../Components/SampleImageModal/SampleImageModal";
import { imageArray } from "../VideoAi/api";
import "./ImageAi.css";
import PromptPanel from "./components/PromptPanel/PromptPanel";
import CreditsPanel from "./components/CreditsPanel/CreditsPanel";
import GeneratedImage from "./components/GeneratedImage/GeneratedImage";
import VersionHistory from "./components/VersionHistory/VersionHistory";
import ImageEditor from "./components/ImageEditor/ImageEditor";
import HowItWorks from "./components/HowItWorks/HowItWorks";


const API_BASE = "https://backend.whalesai.com/api/v1/image-ai";

const authHeaders = () => ({
    headers: { authorization: `Bearer ${localStorage.getItem("aiUser")}` },
});


const IMAGE_MODELS = [
    {
        value: "gpt-image-2.5-sunburst",
        label: "SUNBURST",
        info: "Create breathtaking, cinematic visuals with unmatched detail and artistic excellence.",
        timeSec: 34,
        sample: [
            "/imageAi/SUNBURST1.png",
            "/imageAi/SUNBURST2.png"
        ],
        prompts: [
            "Create a premium cinematic infographic titled “The Evolution of AI”. Show a visual timeline from Early AI to Generative AI with futuristic illustrations, neural networks, microchips, glowing data connections, and clean labels. Use a dark navy, violet, and electric-blue palette with cinematic lighting, detailed visuals, strong hierarchy, and polished editorial infographic design.",

            "Create a futuristic infographic titled “How a Smart City Works”. Show a connected smart city with smart transportation, renewable energy, connected buildings, water management, public safety, and AI data centers. Use callout lines, glowing data networks, realistic 3D architecture, deep blue and violet tones, cinematic lighting, and premium technology-magazine styling."
        ]
    },
    {
        value: "gpt-image-2.5-flare",
        label: "FLARE",
        info: "Generate high-quality images fast, balancing creativity, speed, and cost.",
        timeSec: 22,
        sample: [
            "/imageAi/FLARE1.png",
            "/imageAi/FLARE2.png"
        ],
        prompts: [
            "Create a colorful educational infographic titled “The Science of Sleep”. Show a sleeping person surrounded by Light Sleep, Deep Sleep, REM Sleep, Brain Waves, and Sleep Cycle sections with simple illustrations and icons. Use vibrant purple, blue, coral, and orange gradients, clean typography, friendly visuals, balanced spacing, and modern editorial infographic design.",

            "Create a vibrant educational infographic titled “How a Volcano Erupts”. Show a volcano cross-section with Magma Chamber, Heat and Pressure, Rising Magma, Gas Expansion, Eruption, Ash Cloud, and Lava Flow. Use arrows, geological layers, glowing magma, smoke, and clean information panels with orange, red, yellow, and purple tones."
        ]
    },
    {
        value: "gpt-image-2",
        // label: "IMAGE",
        label: "AURORA",
        info: "Turn ideas into beautiful images instantly with reliable everyday performance.",
        timeSec: 28,
        sample: [
            "/imageAi/AURORA1.png",
            "/imageAi/AURORA2.png"
        ],
        prompts: [
            "Create a clean educational infographic titled “The Water Cycle”. Show Evaporation, Condensation, Precipitation, and Collection using a beautiful landscape with ocean, mountains, clouds, rain, and rivers. Add clear directional arrows and simple icons. Use blue, turquoise, green, and white tones with polished editorial infographic styling, clean typography, balanced composition, and minimal readable text.",

            "Create a professional technology infographic titled “How Electric Vehicles Work”. Show an electric vehicle with Battery Pack, Electric Motor, Inverter, Charging Port, Regenerative Braking, and Control System, plus a simple energy flow diagram. Use a deep-blue background with cyan accents, realistic vehicle details, crisp technical illustrations, clean typography, and modern infographic styling."
        ]
    },
];

const ImageAiNew = () => {
    const { AIuser, userRefetch, setUserRefetch } = useContext(UserAuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    // --- Form state ---
    const [promptForImg, setPromptForImg] = useState("");
    const [aspectRatio, setAspectRatio] = useState("");
    const [examplePrompt, setExamplePrompt] = useState(
        imageArray[Math.floor(Math.random() * imageArray.length)]
    );

    // --- Image Upload state ---
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // --- Prompt suggestion ---
    const [suggestedPrompt, setSuggestedPrompt] = useState("");
    const [checkingPrompt, setCheckingPrompt] = useState(false);
    const promptCheckTimeout = useRef(null);

    const [sessionId, setSessionId] = useState(null);
    const [currentTransactionId, setCurrentTransactionId] = useState(null);
    const [currentImage, setCurrentImage] = useState(null);
    const [imageHistory, setImageHistory] = useState([]);
    const [sessionName, setSessionName] = useState("");

    // --- UI state ---
    const [isGenerating, setIsGenerating] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isAuditing, setIsAuditing] = useState(false);
    const [editMessage, setEditMessage] = useState("");
    const [showImageModal, setShowImageModal] = useState(false);
    const [showMobileEditor, setShowMobileEditor] = useState(false);
    const [isPublishable, setIsPublishable] = useState(false);

    // --- Edit Image Upload state ---
    const [editUploadedImage, setEditUploadedImage] = useState(null);
    const [editImagePreview, setEditImagePreview] = useState(null);

    // --- Drawn Image state ---
    const [drawnImage, setDrawnImage] = useState(null);
    const [drawnImagePreview, setDrawnImagePreview] = useState(null);

    const [imageModel, setImageModel] = useState("");

    const handleEditImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditUploadedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
        e.target.value = ""; // reset input
    };

    const handleRemoveEditImage = (e) => {
        if (e) e.stopPropagation();
        setEditUploadedImage(null);
        setEditImagePreview(null);
    };

    const handleDrawnImage = (file, previewUrl) => {
        setDrawnImage(file);
        setDrawnImagePreview(previewUrl);
    };

    const handleRemoveDrawnImage = () => {
        setDrawnImage(null);
        setDrawnImagePreview(null);
    };

    const chatInputRef = useRef(null);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    // Handle location state (from transaction page)
    useEffect(() => {
        if (location.state?.sessionId) {
            loadSession(location.state.sessionId);
        }
    }, [location.state]);

    useEffect(() => {
        const checkUnviewedAndActive = async () => {
            console.log("Step 4: Checking unviewed_image_ai in localStorage...");
            const unviewedStr = localStorage.getItem("unviewed_image_ai");
            if (unviewedStr) {
                try {
                    const unviewed = JSON.parse(unviewedStr);
                    if (unviewed.length > 0) {
                        const imageData = unviewed[unviewed.length - 1];
                        console.log("Found unviewed image session:", imageData);

                        setSessionId(imageData._id || imageData.sessionId);
                        setCurrentTransactionId(imageData.transactionId);
                        setCurrentImage(imageData.currentImage);
                        setSessionName(imageData.sessionName);
                        setImageHistory(imageData.imageHistory || []);
                        setPromptForImg(imageData.currentPrompt || "");
                        setIsGenerating(false);
                        setIsEditing(false);
                        setIsRegenerating(false);

                        localStorage.removeItem("unviewed_image_ai");

                        const activeStr = localStorage.getItem("active_image_ai");
                        if (activeStr) {
                            try {
                                const active = JSON.parse(activeStr);
                                if (active.transactionId === imageData.transactionId) {
                                    localStorage.removeItem("active_image_ai");
                                }
                            } catch (e) { }
                        }

                        // Mark viewed
                        fetch(`${API_BASE}/mark-viewed`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: `Bearer ${localStorage.getItem("aiUser")}`
                            },
                            body: JSON.stringify({ transactionId: imageData.transactionId })
                        }).catch(err => console.error("Error marking viewed:", err));

                        return;
                    }
                } catch (e) {
                    console.error("Error parsing unviewed data:", e);
                }
            }

            console.log("Step 5: Checking active_image_ai in localStorage...");
            const storedStr = localStorage.getItem("active_image_ai");
            if (!storedStr) return;

            try {
                const stored = JSON.parse(storedStr);
                console.log("Fetching active-status from API for transaction:", stored.transactionId);
                const res = await fetch(`${API_BASE}/active-status`, {
                    method: "GET",
                    headers: { authorization: `Bearer ${localStorage.getItem("aiUser")}` }
                });
                const result = await res.json();
                console.log("Active status response:", result);

                if (result.success && result.isGenerating && result.transactionId === stored.transactionId) {
                    if (stored.action === "edit") setIsEditing(true);
                    else if (stored.action === "regenerate") setIsRegenerating(true);
                    else setIsGenerating(true);
                } else if (result.success && result.status === "completed" && result.data && result.transactionId === stored.transactionId) {
                    setIsGenerating(false);
                    setIsEditing(false);
                    setIsRegenerating(false);
                    const sessionData = result.data;
                    setSessionId(sessionData._id);
                    setCurrentTransactionId(sessionData.transactionId || stored.transactionId);
                    setCurrentImage(sessionData.currentImage);
                    setSessionName(sessionData.sessionName);
                    setImageHistory(sessionData.imageHistory || []);
                    setPromptForImg(sessionData.currentPrompt || "");

                    localStorage.removeItem("active_image_ai");

                    fetch(`${API_BASE}/mark-viewed`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: `Bearer ${localStorage.getItem("aiUser")}`
                        },
                        body: JSON.stringify({ transactionId: result.transactionId })
                    }).catch(err => console.error("Error marking viewed:", err));
                } else if (result.success && !result.isGenerating) {
                    setIsGenerating(false);
                    setIsEditing(false);
                    setIsRegenerating(false);
                    localStorage.removeItem("active_image_ai");
                }
            } catch (error) {
                console.error("Error checking active status:", error);
            }
        };

        checkUnviewedAndActive();

        const handleWakeUp = () => {
            if (document.visibilityState === 'visible' || !document.hidden) {
                checkUnviewedAndActive();
            }
        };

        document.addEventListener("visibilitychange", handleWakeUp);
        window.addEventListener("focus", handleWakeUp);
        window.addEventListener("pageshow", handleWakeUp);

        return () => {
            document.removeEventListener("visibilitychange", handleWakeUp);
            window.removeEventListener("focus", handleWakeUp);
            window.removeEventListener("pageshow", handleWakeUp);
        };
    }, []);

    useEffect(() => {
        const handleBackendTask = (e) => {
            const data = e.detail;
            console.log("Step 6: Backend task event received:", data);
            if (data.type === 'imageAi' && data.status === 'completed') {
                setIsGenerating(false);
                setUserRefetch(!userRefetch);
                setIsEditing(false);
                setIsRegenerating(false);
                const sessionData = data.data || data;
                setSessionId(sessionData._id || sessionData.sessionId);
                setCurrentTransactionId(sessionData.transactionId || data.transactionId);
                setCurrentImage(sessionData.currentImage);
                setSessionName(sessionData.sessionName);
                setImageHistory(sessionData.imageHistory || []);

                const storedStr = localStorage.getItem("active_image_ai");
                if (storedStr) {
                    try {
                        const stored = JSON.parse(storedStr);
                        if (stored.transactionId === data.transactionId) {
                            localStorage.removeItem("active_image_ai");
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }

                fetch(`${API_BASE}/mark-viewed`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${localStorage.getItem("aiUser")}`
                    },
                    body: JSON.stringify({ transactionId: data.transactionId })
                }).catch(err => console.error("Error marking viewed:", err));

                localStorage.removeItem("unviewed_image_ai");
            }
        };

        const handleBackendTaskFailed = (e) => {
            const data = e.detail;
            if (data.type === 'imageAi' && data.status === 'failed') {
                console.log("Backend task failed event received:", data);
                setIsGenerating(false);
                setIsEditing(false);
                setIsRegenerating(false);
                swal({
                    text: data.message || "AI Image generation failed.",
                    icon: "error",
                    buttons: "Ok",
                    className: "modal_class_success",
                });

                const storedStr = localStorage.getItem("active_image_ai");
                if (storedStr) {
                    try {
                        const stored = JSON.parse(storedStr);
                        if (stored.transactionId === data.transactionId) {
                            localStorage.removeItem("active_image_ai");
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }
            }
        };

        window.addEventListener('backend_task_completed', handleBackendTask);
        window.addEventListener('backend_task_failed', handleBackendTaskFailed);

        return () => {
            window.removeEventListener('backend_task_completed', handleBackendTask);
            window.removeEventListener('backend_task_failed', handleBackendTaskFailed);
        };
    }, []);

    const loadSession = async (sid) => {
        try {
            const res = await axios.get(`${API_BASE}/${sid}`, authHeaders());
            const session = res.data?.data;
            if (session) {
                setSessionId(session._id);
                setCurrentTransactionId(session.transactionId);
                setCurrentImage(session.currentImage);
                setSessionName(session.sessionName);
                setImageHistory(session.imageHistory || []);
                setPromptForImg(session.currentPrompt || "");
            }
        } catch (err) {
            console.error("Failed to load session:", err);
            swal({ text: "Failed to load session.", icon: "error", className: "modal_class_success" });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
        e.target.value = ""; // reset input
    };

    const handleRemoveImage = (e) => {
        if (e) e.stopPropagation();
        setUploadedImage(null);
        setImagePreview(null);
    };

    const checkPromptSuggestion = async (value) => {
        if (!value?.trim()) { setSuggestedPrompt(""); return; }
        try {
            setCheckingPrompt(true);
            const res = await axios.post(
                "https://backend.whalesai.com/api/v1/prompt-checker/check",
                { prompt: value, aiService: "image-ai" },
                authHeaders()
            );
            if (res?.data?.success) setSuggestedPrompt(res.data.data.suggested_prompt);
        } catch (err) {
            console.log("Prompt checker error:", err);
        } finally {
            setCheckingPrompt(false);
        }
    };

    // --- Main actions ---
    const handleCreate = async () => {
        if (!promptForImg?.trim()) return;
        if (!imageModel) {
            swal({
                text: "Please select an Image AI model before generating.",
                icon: "warning",
                className: "modal_class_success",
            });
            return;
        }
        if (AIuser.credits < 10) {
            swal({
                text: "You don't have enough credits! Please buy credits to continue.",
                icon: "error", buttons: "Buy", className: "modal_class_success",
            }).then((v) => { if (v) navigate("/purchase"); });
            return;
        }

        setIsGenerating(true);
        setCurrentImage(null);
        setImageHistory([]);
        setSessionId(null);
        setShowMobileEditor(false);
        setDrawnImage(null);
        setDrawnImagePreview(null);
        setIsPublishable(false);

        try {
            const formData = new FormData();
            formData.append("prompt", promptForImg);
            formData.append("model", imageModel);
            formData.append("aspectRatio", aspectRatio);
            if (uploadedImage) {
                formData.append("image", uploadedImage);
            }

            console.log("Step 1: Calling /create API...");
            const res = await axios.post(`${API_BASE}/create`, formData, authHeaders());
            console.log("Step 2: API Response received:", res.data);

            const txId = res.data?.transactionId || res.data?.data?.transactionId;
            if (txId) {
                setCurrentTransactionId(txId);
                console.log("Step 3: Saving transactionId to localStorage:", txId);
                localStorage.setItem("active_image_ai", JSON.stringify({
                    transactionId: txId,
                    aiType: "image-ai",
                    action: "create"
                }));
            } else {
                const data = res.data?.data;
                if (data) {
                    setSessionId(data._id);
                    setCurrentTransactionId(data.transactionId);
                    setCurrentImage(data.currentImage);
                    setSessionName(data.sessionName);
                    setImageHistory(data.imageHistory || []);
                    setUserRefetch(!userRefetch);
                    setIsGenerating(false);
                }
            }
        } catch (err) {
            console.error("Creation Error:", err);
            setIsGenerating(false);
            swal({
                text: err?.response?.data?.message || "Something went wrong! Please try again.",
                icon: "error", buttons: "Ok", className: "modal_class_success",
            });
        }
    };

    const handleRegenerate = async () => {
        if (!sessionId) return;
        if (AIuser.credits <= 10) {
            swal({
                text: "You don't have enough credits!",
                icon: "error", buttons: "Buy", className: "modal_class_success",
            }).then((v) => { if (v) navigate("/purchase"); });
            return;
        }

        setIsRegenerating(true);
        setIsPublishable(false);
        try {
            const res = await axios.post(`${API_BASE}/regenerate`, { sessionId, transactionId: currentTransactionId }, authHeaders());
            console.log("Regenerate API Response:", res.data);

            const txId = res.data?.transactionId || res.data?.data?.transactionId || currentTransactionId;
            if (res.data?.status === true || txId) {
                localStorage.setItem("active_image_ai", JSON.stringify({
                    transactionId: txId,
                    aiType: "image-ai",
                    action: "regenerate"
                }));
            } else {
                const data = res.data?.data;
                if (data) {
                    setCurrentImage(data.currentImage);
                    setImageHistory(data.imageHistory || []);
                    setUserRefetch(!userRefetch);
                }
                setIsRegenerating(false);
            }
        } catch (err) {
            console.error(err);
            setIsRegenerating(false);
            swal({
                text: err?.response?.data?.message || "Regeneration failed.",
                icon: "error", buttons: "Ok", className: "modal_class_success",
            });
        }
    };

    const handleAudit = async () => {
        if (!sessionId) return;
        setIsAuditing(true);
        try {
            const res = await axios.post(`${API_BASE}/audit`, { sessionId }, authHeaders());
            const auditText = res.data?.data?.auditText;
            if (res.data?.data?.isPublishable) {
                setIsPublishable(true);
            }
            if (auditText) {
                setEditMessage(auditText);
                setUserRefetch(!userRefetch);
            }
        } catch (err) {
            console.error("Audit Error:", err);
            swal({
                text: err?.response?.data?.message || "Audit failed.",
                icon: "error", buttons: "Ok", className: "modal_class_success",
            });
        } finally {
            setIsAuditing(false);
        }
    };

    const handleEdit = async () => {
        if (!sessionId || !editMessage?.trim()) return;
        if (AIuser.credits <= 10) {
            swal({
                text: "You don't have enough credits!",
                icon: "error", buttons: "Buy", className: "modal_class_success",
            }).then((v) => { if (v) navigate("/purchase"); });
            return;
        }

        const userMsg = editMessage.trim();
        setEditMessage("");
        setIsEditing(true);
        setIsPublishable(false);

        try {
            const formData = new FormData();
            formData.append("sessionId", sessionId);
            formData.append("prompt", promptForImg);
            formData.append("aspectRatio", aspectRatio);
            formData.append("editMessage", userMsg);
            if (currentTransactionId) {
                formData.append("transactionId", currentTransactionId);
            }
            if (editUploadedImage) {
                formData.append("image", editUploadedImage);
            } else if (drawnImage) {
                formData.append("image", drawnImage);
            }

            setEditUploadedImage(null);
            setEditImagePreview(null);
            setDrawnImage(null);
            setDrawnImagePreview(null);

            const res = await axios.post(
                `${API_BASE}/edit`,
                formData,
                authHeaders()
            );
            console.log("Edit API Response:", res.data);

            const txId = res.data?.transactionId || res.data?.data?.transactionId || currentTransactionId;
            if (res.data?.status === true || txId) {
                localStorage.setItem("active_image_ai", JSON.stringify({
                    transactionId: txId,
                    aiType: "image-ai",
                    action: "edit"
                }));
            } else {
                const data = res.data?.data;
                if (data) {
                    setCurrentImage(data.currentImage);
                    setImageHistory(data.imageHistory || []);
                    setUserRefetch(!userRefetch);
                }
                setIsEditing(false);
            }
        } catch (err) {
            console.error(err);
            setIsEditing(false);
            swal({
                text: err?.response?.data?.message || "Edit failed.",
                icon: "error", buttons: "Ok", className: "modal_class_success",
            });
        }
    };

    const handleDownload = async () => {
        if (!currentImage) return;
        try {
            saveAs(currentImage, `whalesai-image-${Date.now()}.png`);
        } catch {
            window.open(currentImage, "_blank");
        }
    };

    const shuffleExample = () => {
        let next;
        do { next = imageArray[Math.floor(Math.random() * imageArray.length)]; } while (next === examplePrompt);
        setExamplePrompt(next);
    };

    const handlePromptChange = (value, check = true) => {
        setPromptForImg(value);
        if (promptCheckTimeout.current) clearTimeout(promptCheckTimeout.current);
        if (check) {
            promptCheckTimeout.current = setTimeout(() => checkPromptSuggestion(value), 1500);
        }
    };

    const handleClearAll = () => {
        setPromptForImg("");
        setImageModel("");
        setAspectRatio("");
        setUploadedImage(null);
        setImagePreview(null);
        setSuggestedPrompt("");

        setSessionId(null);
        setCurrentTransactionId(null);
        setCurrentImage(null);
        setImageHistory([]);
        setSessionName("");

        setEditMessage("");
        setEditUploadedImage(null);
        setEditImagePreview(null);
        setDrawnImage(null);
        setDrawnImagePreview(null);
        setIsPublishable(false);
    };

    // ======== RENDER ========
    return (
        <Fragment>
            {/* <PageHeading
                title="IMAGE AI"
                description="The Image AI assists in crafting images tailored for social media platforms."
                onInfoClick={() => setShowImageModal(true)}
                infoTooltip="See sample image"
            /> */}
            <SampleImageModal
                imageModal={showImageModal}
                setImageModal={setShowImageModal}
                imageSrc="/image-ai-sample.png"
                imageSrc2="/image-ai-sample2.png"
                title="What you can do?"
                promptText="A futuristic metropolis at night with towering skyscrapers, neon lights, flying vehicles, and a bustling crowd of diverse characters.Uploaded an image with it. Check the uploaded image for reference."
                promptText2="Remove all japanese and chinese texts from the image and change to english only"
            />
            <div>
                <CreditsPanel credits={AIuser?.credits} setShowImageModal={setShowImageModal} />
            </div>
            <div className="imageai-page">

                <div className="imageai-left">

                    <div className="credits-mobile">
                        <HowItWorks />
                    </div>

                    <PromptPanel
                        prompt={promptForImg}
                        onPromptChange={handlePromptChange}

                        imageModel={imageModel}
                        imageModels={IMAGE_MODELS}
                        onImageModelChange={setImageModel}

                        examplePrompt={examplePrompt}
                        onExampleClick={() => handlePromptChange(examplePrompt, false)}
                        onShuffleExample={shuffleExample}

                        suggestedPrompt={suggestedPrompt}
                        checkingPrompt={checkingPrompt}

                        aspectRatio={aspectRatio}
                        onAspectRatioChange={setAspectRatio}

                        loading={isGenerating}
                        isGenerated={!!currentImage}

                        onGenerate={handleCreate}

                        onClear={() => {
                            setPromptForImg("");
                            setSuggestedPrompt("");
                            setUploadedImage(null);
                            setImagePreview(null);
                        }}
                        onClearAll={handleClearAll}
                        imagePreview={imagePreview}
                        onImageChange={handleImageChange}
                        onRemoveImage={handleRemoveImage}
                    />

                    <div className="credits-desktop">
                        <   HowItWorks />
                    </div>

                </div>
                <div className="imageai-center">

                    <div className="generated-editor-combined-card">
                        <GeneratedImage
                            image={currentImage}
                            loading={
                                isGenerating ||
                                isEditing ||
                                isRegenerating
                            }

                            onDownload={handleDownload}
                            onRegenerate={handleRegenerate}
                            onDrawnImage={handleDrawnImage}
                            drawnImagePreview={drawnImagePreview}
                            onRemoveDrawnImage={handleRemoveDrawnImage}
                            onAudit={handleAudit}
                            isAuditing={isAuditing}
                            isPublishable={isPublishable}
                            history={imageHistory}
                            onSelect={setCurrentImage}
                        />

                        <ImageEditor
                            editMessage={editMessage}
                            image={currentImage}
                            setEditMessage={setEditMessage}
                            onSend={handleEdit}
                            loading={isEditing}
                            chatInputRef={chatInputRef}
                            editImagePreview={editImagePreview}
                            onImageChange={handleEditImageChange}
                            onRemoveImage={handleRemoveEditImage}
                        />
                    </div>

                    <VersionHistory
                        history={imageHistory}
                        currentImage={currentImage}
                        onSelect={setCurrentImage}
                    />

                </div>
            </div>

        </Fragment>
    );
};

export default ImageAiNew;
